/**
 * SupportChatWidget — Floating persistent chat widget
 * Renders as a floating button/panel when user has an active chat but is not on /support
 */

import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ChatContainer,
  MessageList,
  Message,
  Avatar,
} from '@chatscope/chat-ui-kit-react';
import '@/styles/chat-kit.css';
import { useAuth } from '../../hooks/useAuth';
import { useSupportChat } from '../../contexts/SupportChatContext';
import { TestableMessageInput } from '../chat/TestableMessageInput';

function getAvatarUrl(name: string, bg = '3b82f6'): string {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${bg}&color=fff&size=128`;
}

export function SupportChatWidget() {
  const location = useLocation();
  const { manager } = useAuth();
  const {
    availability,
    messages,
    sendMessage,
    hasActiveChat,
    isWidgetOpen,
    setWidgetOpen,
  } = useSupportChat();
  const [isSending, setIsSending] = useState(false);

  const isOnSupportPage = location.pathname === '/support';

  const handleSendMessage = useCallback(async (content: string) => {
    if (isSending) return;
    try {
      setIsSending(true);
      await sendMessage(content);
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setIsSending(false);
    }
  }, [sendMessage, isSending]);

  // Only render when there's an active chat and not on the support page
  if (!hasActiveChat || isOnSupportPage) return null;

  const operatorName = availability?.operatorDisplayName ?? 'Support';

  return (
    <div
      data-testid="support-chat-widget"
      className="fixed z-40"
      style={{
        bottom: 'calc(var(--bottom-tab-height, 64px) + env(safe-area-inset-bottom, 0px) + 16px)',
        right: '16px',
      }}
    >
      {isWidgetOpen ? (
        <ExpandedPanel
          operatorName={operatorName}
          availability={availability}
          messages={messages}
          manager={manager}
          isSending={isSending}
          onSendMessage={handleSendMessage}
          onClose={() => setWidgetOpen(false)}
        />
      ) : (
        <CollapsedButton
          operatorName={operatorName}
          onOpen={() => setWidgetOpen(true)}
        />
      )}
    </div>
  );
}

interface CollapsedButtonProps {
  operatorName: string;
  onOpen: () => void;
}

function CollapsedButton({ operatorName, onOpen }: CollapsedButtonProps) {
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="support-chat-widget-toggle"
      aria-label={`Open support chat with ${operatorName}`}
      className="w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all duration-200"
    >
      <ChatBubbleIcon />
    </button>
  );
}

interface ExpandedPanelProps {
  operatorName: string;
  availability: { chatAvailable: boolean; operatorDisplayName?: string; nextAvailableAt?: string } | null;
  messages: Array<{ id: string; content: string; createdAt: string; senderRole: string }>;
  manager: { displayName?: string } | null;
  isSending: boolean;
  onSendMessage: (content: string) => Promise<void>;
  onClose: () => void;
}

function ExpandedPanel({
  operatorName,
  availability,
  messages,
  manager,
  isSending,
  onSendMessage,
  onClose,
}: ExpandedPanelProps) {
  return (
    <div
      className="bg-card border border-border rounded-xl shadow-2xl flex flex-col overflow-hidden"
      style={{ width: '350px', height: '450px' }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-3 flex-shrink-0">
        <span
          className={`w-2 h-2 rounded-full flex-shrink-0 ${availability?.chatAvailable ? 'bg-success' : 'bg-muted-foreground'}`}
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-foreground flex-1 truncate">
          {operatorName}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Minimize chat"
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
          data-testid="support-chat-widget-minimize"
        >
          <MinimizeIcon />
        </button>
      </div>

      {/* Chat body */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ChatContainer style={{ flex: 1, minHeight: 0, width: '100%' }}>
          <MessageList>
            {messages.map((message) => {
              const isOwn = message.senderRole === 'USER';
              const isOperatorOrSystem = message.senderRole === 'OPERATOR' || message.senderRole === 'SYSTEM';
              const avatarBg = isOperatorOrSystem ? '7c3aed' : '3b82f6';
              const senderName = isOwn ? (manager?.displayName ?? 'You') : operatorName;

              return (
                <Message
                  key={message.id}
                  model={{
                    message: message.content,
                    sentTime: new Date(message.createdAt).toISOString(),
                    sender: senderName,
                    direction: isOwn ? 'outgoing' : 'incoming',
                    position: 'normal',
                    type: 'text',
                  }}
                >
                  <Avatar
                    name={senderName}
                    src={getAvatarUrl(senderName, avatarBg)}
                  />
                </Message>
              );
            })}
          </MessageList>
        </ChatContainer>

        <TestableMessageInput
          placeholder="Type a message..."
          onSend={onSendMessage}
          disabled={isSending}
          data-testid="support-widget-send-button"
        />
      </div>
    </div>
  );
}

function ChatBubbleIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function MinimizeIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}
